import React, { useCallback, useEffect, useRef, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Spinner from "react-bootstrap/Spinner";
import Tooltip from "react-bootstrap/Tooltip";

import { connectWhatsApp } from "../../whatsappApi";
import DisconnectModal from "./DisconnectModal";

/**
 * WhatsAppConnectCard — Meta Embedded Signup for one practice.
 *
 * Visual conventions: /CLAUDE.md §1.5 (info callout), §1.4 (buttons),
 * §1.9 (badges).
 *
 * The acknowledgement checkbox is deliberate friction on the one step in this
 * integration that cannot be undone: moving a practice's number to the Cloud
 * API removes WhatsApp from the phone it is on and deletes that phone's chat
 * history permanently. Discovering that afterwards turns a successful
 * integration into a support incident on day one.
 */

const FB_SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";

function loadFacebookSdk() {
  if (window.FB) return Promise.resolve(window.FB);
  if (window.__fbSdkPromise) return window.__fbSdkPromise;

  window.__fbSdkPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById("facebook-jssdk");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.FB));
      return;
    }
    const script = document.createElement("script");
    script.id = "facebook-jssdk";
    script.src = FB_SDK_SRC;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.onload = () => {
      window.FB.init({
        appId: process.env.REACT_APP_META_APP_ID,
        cookie: true,
        xfbml: false,
        version: process.env.REACT_APP_META_GRAPH_VERSION || "v21.0",
      });
      resolve(window.FB);
    };
    script.onerror = () => reject(new Error("Could not load the Meta SDK"));
    document.body.appendChild(script);
  });

  return window.__fbSdkPromise;
}

const QUALITY_VARIANT = { GREEN: "success", YELLOW: "warning", RED: "danger" };

export default function WhatsAppConnectCard({
  practiceNumber,
  practiceName,
  account,
  canConfigure,
  onChanged,
  showToast,
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState(null);
  const [showDisconnect, setShowDisconnect] = useState(false);

  // The popup reports the WABA via a window message while FB.login returns the
  // code through its callback. They arrive in either order, so both are held
  // until the pair is complete.
  const signupRef = useRef({ code: null, wabaId: null, phoneNumberId: null });
  const finishRef = useRef(null);

  const finishConnect = useCallback(async () => {
    const { code, wabaId, phoneNumberId } = signupRef.current;
    if (!code || !wabaId || !phoneNumberId) return;

    signupRef.current = { code: null, wabaId: null, phoneNumberId: null };

    try {
      await connectWhatsApp({ practiceNumber, code, wabaId, phoneNumberId });
      showToast?.("success", "WhatsApp connected.");
      onChanged?.();
    } catch (err) {
      // Meta's own message is the useful one here — a two-step verification
      // PIN clash tells the practice exactly what to fix on their side.
      setError(
        err?.response?.data?.error ||
          "Could not complete the WhatsApp connection.",
      );
    } finally {
      setLaunching(false);
    }
  }, [practiceNumber, onChanged, showToast]);

  finishRef.current = finishConnect;

  // Registered before FB.login is ever called, per Meta's flow.
  useEffect(() => {
    function onMessage(event) {
      if (!String(event.origin).endsWith("facebook.com")) return;
      let payload = event.data;
      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch (_e) {
          return;
        }
      }
      if (payload?.type !== "WA_EMBEDDED_SIGNUP") return;

      if (payload.event === "FINISH") {
        signupRef.current.wabaId = payload.data?.waba_id || null;
        signupRef.current.phoneNumberId = payload.data?.phone_number_id || null;
        finishRef.current?.();
      } else if (payload.event === "CANCEL") {
        // Closing the popup is not an error — just reset.
        signupRef.current = { code: null, wabaId: null, phoneNumberId: null };
        setLaunching(false);
      }
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const launch = async () => {
    setError(null);
    setLaunching(true);
    try {
      const FB = await loadFacebookSdk();
      FB.login(
        (response) => {
          if (!response?.authResponse?.code) {
            setLaunching(false);
            return;
          }
          signupRef.current.code = response.authResponse.code;
          finishRef.current?.();
        },
        {
          config_id: process.env.REACT_APP_META_WA_CONFIG_ID,
          response_type: "code",
          override_default_response_type: true,
          extras: {
            feature: "whatsapp_embedded_signup",
            sessionInfoVersion: 3,
          },
        },
      );
    } catch (err) {
      setError(err.message || "Could not open the Meta sign-up window.");
      setLaunching(false);
    }
  };

  const connected = account?.connected;
  const needsReconnect = account?.status === "token_invalid";

  if (connected && !needsReconnect) {
    return (
      <>
        <div className="acc-list mb-3">
          <div className="d-flex flex-wrap align-items-start gap-2 mb-2">
            <div>
              <h6 className="mb-0 text-primary fw-bold">
                {account.verifiedName || "WhatsApp Business"}
              </h6>
              <span className="small text-muted">
                {account.displayPhoneNumber}
              </span>
            </div>
            <div className="ms-auto d-flex align-items-center gap-2">
              {account.qualityRating && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      Meta's quality rating for this number, based on how
                      patients respond to your messages.
                    </Tooltip>
                  }
                >
                  <Badge bg={QUALITY_VARIANT[account.qualityRating] || "secondary"}>
                    {account.qualityRating}
                  </Badge>
                </OverlayTrigger>
              )}
              {account.messagingLimit && (
                <Badge bg="secondary">{account.messagingLimit}</Badge>
              )}
            </div>
          </div>

          {account.tokenExpiresAt && (
            <Alert variant="warning" className="py-2 px-3 mb-2 small">
              This connection expires on{" "}
              {new Date(account.tokenExpiresAt).toLocaleDateString("en-GB")}. It
              renews automatically. You will be notified if it needs attention.
            </Alert>
          )}

          {canConfigure && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => setShowDisconnect(true)}
            >
              <i className="fa-solid fa-link-slash me-2" />
              Disconnect
            </Button>
          )}
        </div>

        <DisconnectModal
          show={showDisconnect}
          onHide={() => setShowDisconnect(false)}
          practiceNumber={practiceNumber}
          practiceName={practiceName || account.verifiedName}
          onDisconnected={onChanged}
          showToast={showToast}
        />
      </>
    );
  }

  return (
    <div className="acc-list mb-3">
      {needsReconnect && (
        <Alert variant="warning" className="mb-3">
          The WhatsApp connection for this practice needs to be re-authorised.
          Appointment reminders will not send until it is reconnected.
        </Alert>
      )}

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="posted-invoice-info mb-3">
        <h6 className="mb-2 text-primary fw-bold">Before you connect</h6>
        <p className="mb-2">
          <span className="fw-semibold">Use a new number.</span> Your existing
          WhatsApp keeps working on the phone at reception. Patients will need
          to be told the new number.
        </p>
        <p className="mb-0">
          <span className="fw-semibold">Move your current number.</span>{" "}
          Patients keep using the number they know, but WhatsApp will be removed
          from the phone it is on now and{" "}
          <span className="fw-semibold">
            that phone's chat history will be permanently deleted
          </span>
          . Export your chats first if you want to keep them.
        </p>
      </div>

      {canConfigure ? (
        <>
          <Form.Check
            type="checkbox"
            id="wa-ack"
            className="mb-3"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            label="I understand how the number I choose will be used"
          />
          <Button
            variant="primary"
            disabled={!acknowledged || launching}
            onClick={launch}
          >
            {launching ? (
              <Spinner size="sm" animation="border" />
            ) : (
              <>
                <i className="fa-brands fa-whatsapp me-2" />
                {needsReconnect ? "Reconnect WhatsApp" : "Connect WhatsApp"}
              </>
            )}
          </Button>
        </>
      ) : (
        <p className="mb-0">
          <small>
            Only a practice administrator can connect WhatsApp for this
            practice.
          </small>
        </p>
      )}
    </div>
  );
}
